import PropTypes from 'prop-types';
import { Component } from 'react';
import { withRouter } from 'react-router-dom';
import Errored from '../../core/Errored';
import Loading from '../../core/Loading';
import Locked from '../../core/Locked';
import Notification from '../../core/Notification';
import VersionWarning from '../../core/VersionWarning';
import ProjectProvider from '../../core/providers/ProjectProvider';
import { getGraphFromProject, makeCancelable, sortObjects } from '../../core/utils/utils';
import withGS from '../../core/withGShoc';
import GS from '../../grafsync/grafsync';

const loadOptions = {};

class OntologyProjectInfoContainer extends Component {

  constructor(props) {
    super(props);
    this.state = {
      initialLoad: true,
      isLoading: true,
      infoLoading: true,
      ontologies: [],
      ontologyProjects: [],
      projectUsers: undefined,
      isErrored: undefined,
      entitlements: undefined
    };
    this.cancellable = null;
  }

  static getDerivedStateFromProps(props, state) {
    if (props.data._version !== state._version) {
      return {
        infoLoading: true,
        isLoading: false,
        _version: props.data._version,
      };
    }
    return null;
  }

  componentDidUpdate() {
    if (!this.state.isErrored && this.state.infoLoading === true) {
      this.collectProjectData();
    }
  }

  componentDidMount() {
    this.collectProjectData();
  }

  loadData(iris, options) {
    this.cancellable = makeCancelable(GS.loadData(iris, options, this.props.graph, false));
    return this.cancellable;
  }

  loadModel(graph) {
    this.cancellable = makeCancelable(GS.loadModel(graph));
    return this.cancellable;
  }

  loadEntitlements() {
    this.cancellable = makeCancelable(GS.getProjectEntitlements(this.props.user, this.props.project));
    return this.cancellable;
  }

  componentWillUnmount() {
    if (this.cancellable) this.cancellable.cancel();
  }

  doGetUsers() {
    this.cancellable = makeCancelable(GS.subscriptionUsers(this.props.id));
    return this.cancellable;
  }

  collectProjectData() {
    if (this.state.isLoading) return;
    this.setState({ isLoading: true }, async () => {
      try {
        // We only load these once - they won't change very often at all
        if (this.state.entitlements === undefined && !this.props.omitEntitlements) {
          const entitlements = await this.loadEntitlements().promise;
          if (this.cancellable && this.cancellable.isCancelled()) return;
          const users = await this.doGetUsers().promise;
          if (this.cancellable && this.cancellable.isCancelled()) return;
          const projectUsers = users.map(user => ({
            value: user.id,
            label: user.givenName && user.familyName ?
              user.givenName + " " + user.familyName
              :
              user.username
          })).sort(function (a, b) {
            return a.label.toLowerCase() < b.label.toLowerCase() ? -1 : 1;
          });
          this.setState({ entitlements: entitlements.hasEntitlement, projectUsers });
        }
        if (this.props.data.hasComponent || this.props.data.ontologyProjectAssociation) {
          const opa = this.props.data.ontologyProjectAssociation ? this.props.data.ontologyProjectAssociation : [];
          const cpt = this.props.data.hasComponent ? this.props.data.hasComponent : [];
          const resources = [...opa, ...cpt];
          const mergedLoadOptions = Object.assign({}, loadOptions);
          if (resources.length > 0) {
            await this.loadData(resources, mergedLoadOptions).promise;
            if (this.cancellable && this.cancellable.isCancelled()) return;
          }
          await this.processProjects();
          if (this.cancellable && this.cancellable.isCancelled()) return;
          this.processComponents();
        }
      }
      catch (e) {
        if (this.cancellable && this.cancellable.isCancelled()) return;
        this.setState({ isErrored: e });
      }
      this.setState({ isLoading: false, infoLoading: false, initialLoad: false });
    });
  }

  async processProjects() {
    const { ontologyProjectAssociation } = this.props.data;
    if (ontologyProjectAssociation) {
      let ontologyProjects = [];
      const fetchModels = ontologyProjectAssociation.map(opa => {
        const opaData = GS.data(opa, this.props.graph);
        ontologyProjects.push(opaData.associatedProject);
        // We load models so we see updates to it
        return this.loadModel(getGraphFromProject(opaData.associatedProject)).promise;
      });
      await Promise.all(fetchModels);
      if (this.cancellable && this.cancellable.isCancelled()) return;
      this.setState({ ontologyProjects });
    }
  }


  processComponents() {
    try {
      if (this.props.data.hasComponent) {
        let ontologies = [];
        this.props.data.hasComponent.forEach(component => {
          const componentData = GS.data(component, this.props.graph, false);
          if (componentData) {
            if (componentData.type.includes("Ontology")) {
              ontologies.push({
                id: componentData.id,
                title: componentData.title,
                type: componentData.type,
                description: componentData.description,
                editingOptions: componentData.editingOption
              });
            }
          }
        });
        sortObjects(ontologies, 'title', this.props.data.defaultLang, "und", false);
        this.setState({ ontologies });
      }
    }
    catch (e) {
      if (this.cancellable && !this.cancellable.isCancelled()) this.setState({ isErrored: e });
    }
  }

  addComponent = async (title, componentIRI, isSlashIRI, description, lang = "en", isOrdered, isUuidIRI) => {
    let newComponent = {
      id: componentIRI,
      type: "Ontology",
      title: { [lang]: title },
      useSlashIRI: isSlashIRI,
      useUuidIRI: isUuidIRI,
      minimiseEncoding: true,
      editingOption: ["OWL DL"],
      hiddenProp: [
        "http://www.w3.org/2000/01/rdf-schema#seeAlso",
        "http://www.w3.org/2004/02/skos/core#definition",
        "http://www.w3.org/2004/02/skos/core#changeNote",
        "http://www.w3.org/2004/02/skos/core#editorialNote",
        "http://www.w3.org/2004/02/skos/core#example",
        "http://www.w3.org/2004/02/skos/core#note",
        "http://www.w3.org/2004/02/skos/core#hiddenNote",
        "http://www.w3.org/2004/02/skos/core#historyNote",
        "http://www.w3.org/2004/02/skos/core#altLabel",
        "http://www.w3.org/2004/02/skos/core#prefLabel",
        "http://www.w3.org/2004/02/skos/core#scopeNote"
      ],
      ...(description && description !== "") && { description: { [lang]: description } }
    };
    await GS.insertResource(newComponent, componentIRI, this.props.graph);
    GS.addProperty(this.props.project, this.props.graph, 'hasComponent', componentIRI);
  };

  deleteComponent = (iri, link) => {
    GS.deleteResource(iri, this.props.graph);
    this.props.history.push(link);
  };

  monitorLinkUpdate = (iri, property) => {
    if (property === "title") this.processComponents();
  };

  render() {

    const { id, data, passthroughComponent, graph, projectType, lockedGraphs, omitEntitlements } = this.props;
    const { isLoading, isErrored, ontologies, ontologyProjects, entitlements, initialLoad, projectUsers } = this.state;

    if (lockedGraphs && ontologyProjects && lockedGraphs.length > 0 && ontologyProjects.length > 0) {
      const shouldLock = lockedGraphs.some(lg => ontologyProjects.some(op => getGraphFromProject(op) === lg));
      if (shouldLock) return <Locked />;
    }

    if (data.isVersionOfProject) return <VersionWarning />;
    if (projectType && projectType !== data.type) return <Notification spaceRight={true} text="You are trying to view an incorrect project type." />;
    if (data.pendingDeletion) return <Notification spaceRight={true} dataTest="pending-deletion" text="This project is pending deletion." />;
    if (isErrored) return <Errored dataTest="ontologycontainer-problem" data={isErrored} />;
    if (isLoading && initialLoad) return <Loading offset="40px" size="large" message="Loading project data..." />;
    if (!entitlements && !omitEntitlements) return null;

    const WrappedComponent = passthroughComponent;

    return (
      <ProjectProvider value={data}>
        <WrappedComponent
          {...this.props}
          id={id}
          graph={graph}
          project={id}
          data={data}
          projectUsers={projectUsers}
          entitlements={entitlements}
          ontologies={ontologies}
          existingComponents={[].concat(ontologies)}
          components={ontologies}
          addComponent={this.addComponent}
          onDeleteComponent={this.deleteComponent}
          monitorLinkUpdate={this.monitorLinkUpdate}
        />
      </ProjectProvider>
    );
  }
}

OntologyProjectInfoContainer.propTypes = {
  omitEntitlements: false
};

OntologyProjectInfoContainer.propTypes = {
  data: PropTypes.object.isRequired,
  id: PropTypes.string.isRequired,
  history: PropTypes.object.isRequired,
  project: PropTypes.string.isRequired,
  lockedGraphs: PropTypes.array,
  projectType: PropTypes.string,
  user: PropTypes.object.isRequired,
  graph: PropTypes.string.isRequired
};

export default withRouter(withGS(OntologyProjectInfoContainer));